import { createColumnHelper } from '@tanstack/react-table';

import type { MeasurementDTO } from '@invenco/common-interface/shared';
import type { SkuDTO, UnitDTO } from '@invenco/products-interface';

import { Badge } from '@/components/core/badge';
import { Card, CardHeader } from '@/components/core/card';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { formatNumber } from '@/shared/helpers';

type Props = {
  sku?: SkuDTO;
  isLoading?: boolean;
};

const columnHelper = createColumnHelper<UnitDTO>();

function formatVerifiedDimensions(verifiedMeasurement?: MeasurementDTO | null) {
  if (!verifiedMeasurement) return undefined;
  return `${formatNumber(verifiedMeasurement.length)} x ${formatNumber(verifiedMeasurement.width)} x ${formatNumber(verifiedMeasurement.height)} ${verifiedMeasurement.dimensionUnit.toLowerCase()}`;
}

function formatVerifiedWeight(verifiedMeasurement?: MeasurementDTO | null) {
  if (!verifiedMeasurement) return undefined;
  return `${formatNumber(verifiedMeasurement.weight)} ${verifiedMeasurement.weightUnit.toLowerCase()}`;
}

const columns = [
  columnHelper.accessor('unitType', {
    header: 'Type',
    cell: ({ getValue }) => <Badge variant="secondary">{getValue()}</Badge>,
  }),
  columnHelper.accessor('unitOfMeasure', {
    header: 'Measure',
    cell: ({ getValue }) => <Badge variant="secondary">{getValue()}</Badge>,
  }),
  columnHelper.accessor('baseQuantity', {
    header: 'Qty',
    cell: ({ getValue }) => formatNumber(getValue()),
    meta: { align: 'right' },
  }),
  columnHelper.accessor('barcode', {
    header: 'Barcode',
  }),
  columnHelper.accessor('verifiedMeasurement', {
    id: 'verifiedDimensions',
    header: 'Verified dimensions (L x W x H)',
    cell: ({ getValue }) => formatVerifiedDimensions(getValue()),
  }),
  columnHelper.accessor('verifiedMeasurement', {
    id: 'verifiedWeight',
    header: 'Verified weight',
    cell: ({ getValue }) => formatVerifiedWeight(getValue()),
  }),
];

export function UnitsTable({ sku, isLoading }: Props) {
  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'sku-details-units',
    defaultPageSize: 10,
  });

  const table = useDataTable({
    data: sku?.units ?? [],
    columns,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    state,
    onSortingChange,
  });

  return (
    <Card aria-label="Units">
      <CardHeader>
        <DataTableCardTitle table={table}>Units</DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table}>
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
